import { ArrowRight, Building, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BrandMark } from "../../components/BrandMark";
import { ApiError, apiFetch } from "../../lib/api";
import { authClient } from "../../lib/auth-client";

interface SsoOptions {
  enabled: boolean;
  tenant: string | null;
  provider: "directus" | null;
  label: string | null;
}

type LoginMode = "local" | "directus";

export function AdminLoginPage() {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [ssoOptions, setSsoOptions] = useState<SsoOptions | null>(null);
  const [mode, setMode] = useState<LoginMode>("local");
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const directusRequested = params.has("directus");

  useEffect(() => {
    apiFetch<SsoOptions>("/api/auth/directus/options")
      .then((options) => {
        setSsoOptions(options);
        if (directusRequested && options.enabled) setMode("directus");
      })
      .catch(() => setSsoOptions(null));
  }, [directusRequested]);

  const afterSignIn = () => {
    navigate(params.get("next") || "/admin", { replace: true });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const result = await authClient.signIn.email({
      email: String(form.get("email")),
      password: String(form.get("password")),
      rememberMe: true,
    });
    setBusy(false);
    if (result.error) {
      setError("E-Mail oder Passwort stimmen nicht.");
      return;
    }
    afterSignIn();
  };

  const submitDirectus = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await apiFetch("/api/auth/directus/sign-in", {
        method: "POST",
        body: JSON.stringify({
          email: String(form.get("email")),
          password: String(form.get("password")),
          ...(String(form.get("otp") || "")
            ? { otp: String(form.get("otp")) }
            : {}),
        }),
      });
      afterSignIn();
    } catch (caught) {
      setBusy(false);
      const code = caught instanceof ApiError ? (caught.code ?? "") : "";
      if (code === "OTP_REQUIRED" || code === "OTP_INVALID") {
        setOtpRequired(true);
        setError(
          code === "OTP_INVALID"
            ? "Der 2FA-Code ist nicht gültig."
            : "Bitte gib deinen 2FA-Code ein.",
        );
        return;
      }
      setOtpRequired(false);
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : "Anmeldung fehlgeschlagen. Bitte versuche es erneut.",
      );
    }
  };

  const ssoEnabled = ssoOptions?.enabled && ssoOptions.provider === "directus";

  return (
    <div className="auth-page">
      <section className="auth-page__visual">
        <BrandMark inverse />
        <div>
          <span className="auth-page__live">
            <i /> Redaktion live
          </span>
          <h1>
            Der ruhige Ort für laute <em>Momente.</em>
          </h1>
          <p>
            Posten, planen, freigeben und den Abend im Blick behalten – auch
            wenn das Netz kurz nicht mitspielt.
          </p>
        </div>
      </section>
      <main id="main" className="auth-card">
        {ssoEnabled && (
          <div
            className="auth-card__tabs"
            role="tablist"
            aria-label="Anmeldeart"
          >
            <button
              type="button"
              className={mode === "local" ? "is-active" : ""}
              aria-pressed={mode === "local"}
              onClick={() => {
                setMode("local");
                setError("");
                setOtpRequired(false);
              }}
            >
              <KeyRound size={15} /> Redaktionskonto
            </button>
            <button
              type="button"
              className={mode === "directus" ? "is-active" : ""}
              aria-pressed={mode === "directus"}
              onClick={() => {
                setMode("directus");
                setError("");
                setOtpRequired(false);
              }}
            >
              <Building size={15} /> {ssoOptions?.label ?? "Directus"}
            </button>
          </div>
        )}

        {ssoEnabled && mode === "directus" && (
          <div>
            <h2>Mit dem U2tour-Zugang anmelden.</h2>
            <p>
              Dieselben Zugangsdaten wie im U2tour-CMS – du brauchst kein
              zweites Konto.
            </p>
          </div>
        )}
        {(!ssoEnabled || mode === "local") && (
          <div>
            <h2>Willkommen zurück.</h2>
            <p>Melde dich mit deinem Redaktionskonto an.</p>
          </div>
        )}

        {(!ssoEnabled || mode === "local") && (
          <form onSubmit={submit}>
            <label>
              <span>E-Mail</span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            <label>
              <span>Passwort</span>
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={12}
              />
            </label>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? "Wird geprüft…" : "Anmelden"} <ArrowRight size={17} />
            </button>
          </form>
        )}

        {ssoEnabled && mode === "directus" && (
          <form onSubmit={submitDirectus}>
            <label>
              <span>E-Mail</span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
              />
            </label>
            <label>
              <span>Passwort</span>
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
            {otpRequired && (
              <label>
                <span>2FA-Code</span>
                <input
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="5-stelliger Code"
                />
              </label>
            )}
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? "Wird geprüft…" : "Anmelden"} <ArrowRight size={17} />
            </button>
          </form>
        )}

        {ssoEnabled && mode === "directus" && (
          <p className="auth-card__hint">
            <ShieldCheck size={14} aria-hidden="true" />
            Deine Zugangsdaten werden ausschließlich serverseitig gegen
            zoocore.u2tour.de geprüft und nicht gespeichert.
          </p>
        )}

        {(!ssoEnabled || mode === "local") && (
          <Link to="/admin/reset-password" className="auth-card__reset">
            <KeyRound size={15} /> Passwort vergessen?
          </Link>
        )}
        <small>
          Geschützter Redaktionsbereich · Sitzungen werden sicher verwaltet
        </small>
      </main>
    </div>
  );
}
